import React, { useState, useRef, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import './ProfilePhotoUpload.css';
import { BaseUrl } from '../../BaseUrl';

const ProfilePhotoUpload = ({
  value,
  onChange,
  label = 'Profile Photo',
  required = false,
  error = '',
  maxSizeMB = 5,
  minDimension = 320,
  apiBase = BaseUrl
}) => {
  const [preview, setPreview] = useState(value || null);
  const [uploading, setUploading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // Cropper state
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const fileInputRef = useRef(null);

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  const validateFile = (file) => {
    if (!allowedTypes.includes(file.type)) {
      return 'Please upload a JPG, PNG, or WebP image';
    }
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return `Image must be smaller than ${maxSizeMB}MB`;
    }
    return null;
  };

  const validateImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        if (img.width < minDimension || img.height < minDimension) {
          resolve(`Image must be at least ${minDimension}x${minDimension} pixels`);
        } else {
          resolve(null);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        resolve('Unable to read image file');
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    setValidationError('');

    const fileError = validateFile(file);
    if (fileError) {
      setValidationError(fileError);
      return;
    }

    const dimensionError = await validateImageDimensions(file);
    if (dimensionError) {
      setValidationError(dimensionError);
      return;
    }

    // Read file and open cropper
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageToCrop(e.target.result);
      setShowCropper(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Create cropped image from canvas
  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = new Image();
    image.src = imageSrc;

    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Set canvas size to the crop size
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    // Convert canvas to blob
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        'image/jpeg',
        0.9
      );
    });
  };

  const handleCropApply = async () => {
    if (!croppedAreaPixels || !imageToCrop) return;

    try {
      const croppedBlob = await getCroppedImg(imageToCrop, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], 'profile-photo.jpg', { type: 'image/jpeg' });

      // Create preview
      const previewUrl = URL.createObjectURL(croppedBlob);
      setPreview(previewUrl);

      // Close cropper
      setShowCropper(false);
      setImageToCrop(null);

      // Upload the cropped file
      await uploadFile(croppedFile);
    } catch (error) {
      console.error('Crop error:', error);
      setValidationError('Failed to crop image. Please try again.');
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFile = async (file) => {
    setUploading(true);

    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('token');
      const endpoint = token
        ? `${apiBase}/api/v1/upload-image/upload`
        : `${apiBase}/api/v1/upload-image/signup-upload`;

      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
        headers,
      });

      const data = await response.json();

      if (data.success || data.url || data.imageData?.url) {
        const imageUrl = data.url || data.imageData?.url || data.secure_url;
        onChange(imageUrl);
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setValidationError('Failed to upload image. Please try again.');
      setPreview(value || null);
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, []);

  const handleClick = () => {
    if (!showCropper) {
      fileInputRef.current?.click();
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setPreview(null);
    onChange('');
    setValidationError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const displayError = validationError || error;

  return (
    <div className="profile-photo-upload">
      {label && (
        <label className="upload-label">
          {label}
          {required && <span className="required-asterisk">*</span>}
        </label>
      )}

      <div
        className={`upload-zone ${isDragging ? 'dragging' : ''} ${displayError ? 'has-error' : ''} ${preview ? 'has-preview' : ''}`}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleInputChange}
          className="file-input"
        />

        {preview ? (
          <div className="preview-container">
            <img src={preview} alt="Profile preview" className="preview-image" />
            <div className="preview-overlay">
              <button type="button" className="change-photo-btn">
                <i className="bi bi-camera"></i>
                <span>Change</span>
              </button>
              <button
                type="button"
                className="remove-photo-btn"
                onClick={handleRemove}
              >
                <i className="bi bi-trash"></i>
              </button>
            </div>
          </div>
        ) : (
          <div className="upload-placeholder">
            <div className="upload-icon">
              <i className="bi bi-camera"></i>
            </div>
            <p className="upload-text">
              {isDragging ? 'Drop image here' : 'Click or drag to upload'}
            </p>
            <p className="upload-hint">
              JPG, PNG or WebP. Min {minDimension}x{minDimension}px. Max {maxSizeMB}MB.
            </p>
          </div>
        )}

        {uploading && (
          <div className="upload-progress-overlay">
            <div className="upload-spinner"></div>
            <span>Uploading...</span>
          </div>
        )}
      </div>

      {displayError && <div className="upload-error">{displayError}</div>}

      {/* Crop Modal */}
      {showCropper && imageToCrop && (
        <div className="crop-modal-overlay">
          <div className="crop-modal">
            <div className="crop-modal-header">
              <h3>Adjust Your Photo</h3>
              <p>Drag to reposition, use slider to zoom</p>
            </div>

            <div className="crop-container">
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>

            <div className="crop-controls">
              <div className="zoom-control">
                <i className="bi bi-dash-lg"></i>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="zoom-slider"
                />
                <i className="bi bi-plus-lg"></i>
              </div>
            </div>

            <div className="crop-modal-actions">
              <button
                type="button"
                className="crop-cancel-btn"
                onClick={handleCropCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="crop-apply-btn"
                onClick={handleCropApply}
              >
                <i className="bi bi-check-lg"></i>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePhotoUpload;
